import httpClient from "@utils/httpClient";

/**
 * Daily Plans & Reports API.
 *
 * httpClient already unwraps response.data, so `response.success` / `.data` /
 * `.error` are top-level. These functions never throw — they always resolve to
 * { success, data, error }.
 */

const qs = (params = {}) => {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

/**
 * The daily plan board for one date.
 * @param {{date?: string, deptId?: string|number, search?: string, scope?: 'org'|'team'}} params
 */
export const getPlanBoard = async (params = {}) => {
  try {
    const response = await httpClient.get(`/hr/plan-reports/board${qs(params)}`);
    return { success: response.success, data: response.data || null, error: response.error };
  } catch (error) {
    console.error('getPlanBoard error:', error);
    return { success: false, data: null, error: { message: error.message } };
  }
};

/** One employee's tasks + note for a date. */
export const getEmployeeDay = async (employeeId, date, scope) => {
  try {
    const response = await httpClient.get(
      `/hr/plan-reports/employee/${employeeId}/day${qs({ date, scope })}`
    );
    return { success: response.success, data: response.data || null, error: response.error };
  } catch (error) {
    console.error('getEmployeeDay error:', error);
    return { success: false, data: null, error: { message: error.message } };
  }
};

/** The full carry history of one task. */
export const getTaskChain = async (taskId) => {
  try {
    const response = await httpClient.get(`/hr/plan-reports/task/${taskId}/chain`);
    return { success: response.success, data: response.data || null, error: response.error };
  } catch (error) {
    console.error('getTaskChain error:', error);
    return { success: false, data: null, error: { message: error.message } };
  }
};

/** Per-employee scorecards over [from, to). `to` is EXCLUSIVE. */
export const getScorecards = async (params = {}) => {
  try {
    const response = await httpClient.get(`/hr/plan-reports/employees${qs(params)}`);
    return { success: response.success, data: response.data || null, error: response.error };
  } catch (error) {
    console.error('getScorecards error:', error);
    return { success: false, data: null, error: { message: error.message } };
  }
};

/** Aging tasks, chronic slippers, stale-open zombies, blocker reasons. */
export const getSlippage = async (params = {}) => {
  try {
    const response = await httpClient.get(`/hr/plan-reports/slippage${qs(params)}`);
    return { success: response.success, data: response.data || null, error: response.error };
  } catch (error) {
    console.error('getSlippage error:', error);
    return { success: false, data: null, error: { message: error.message } };
  }
};

/** Completion trend + department / manager rollups + workload spread. */
export const getAnalytics = async (params = {}) => {
  try {
    const response = await httpClient.get(`/hr/plan-reports/analytics${qs(params)}`);
    return { success: response.success, data: response.data || null, error: response.error };
  } catch (error) {
    console.error('getAnalytics error:', error);
    return { success: false, data: null, error: { message: error.message } };
  }
};

/**
 * Download a report as CSV / XLSX / PDF.
 *
 * Uses raw fetch, NOT httpClient: the interceptor JSON-parses every response and
 * would corrupt the binary. Mirrors PayrollService.downloadSalarySlipPDF.
 *
 * @param {'board'|'employees'|'slippage'} report
 * @param {'csv'|'xlsx'|'pdf'} format
 * @param {object} filters
 */
export const downloadPlanReport = async (report, format, filters = {}) => {
  try {
    const baseURL =
      process.env.REACT_APP_API_BASE_URL || 'https://nucleus.api.milekraft.com/api';
    const token = localStorage.getItem('authToken');
    const url = `${baseURL}/hr/plan-reports/export${qs({ ...filters, report, format })}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      // The server sends JSON for failures (row caps, bad range, PDF unavailable).
      let message = 'Failed to generate the export';
      try {
        const body = await response.json();
        message = body?.error?.message || message;
      } catch {
        /* non-JSON body — keep the default */
      }
      throw new Error(message);
    }

    const disposition = response.headers.get('Content-Disposition');
    let filename = `${report}.${format}`;
    if (disposition) {
      const match = /filename="([^"]*)"/.exec(disposition);
      if (match && match[1]) filename = match[1];
    }

    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);

    return { success: true, data: { filename }, error: null };
  } catch (error) {
    console.error('downloadPlanReport error:', error);
    return { success: false, data: null, error: { message: error.message } };
  }
};
